import React, { useEffect, useState } from "react";
import moment from "moment";

import SelectBox from "../../../../../../components/select-box/select-box";
import CalendarBox from "../../../../../../components/calendar-box/calendar-box";
import InputBox from "../../../../../../components/input-box/input-box";
import PhoneBox from "../../../../../../components/phone-box/phone-box";
import SearchBox from "../../../../../../components/search-box-mediadores/search-box";
import RepreAddress from "./repre-data-address";
import { RepreDataContainer } from "./repre-data-style";

import { withGeneral } from "../../../../../../context/mixin/with-general";
import { DOC_NUMBER_MAX_LENGTH } from "../../../../../../constants";
import { filterCountries } from "../../../../../../utils/countries";
import { validateData } from "./repre-data-validations";
import { emptyLegalGuardianErrors } from "./repre-data-helpers";
import { getValidFormattedData, validateNoErrors } from "../address-item/helpers";
import { getDocumentTypes } from "../../../../../../utils/validation";
import { DocType, ErrorObj, LegalRepreDataProps } from "./repre-data-types";
import { AddressDataDto, DefaultAddressDataDto } from "../address-item/address-form-item-dto";

const LegalRepreData = (props: LegalRepreDataProps) => {
  const [errors, setErrors] = useState<ErrorObj>(emptyLegalGuardianErrors);
  const [addressObj, setAddressObj] = useState<AddressDataDto>(
    DefaultAddressDataDto({ defaultCountry: { value: "ESPAÑA", code: "ESPA" } }),
  );
  const [nationalityLabel, setNationalityLabel] = useState<string>("");

  useEffect(() => {
    if (props.validate.validateRequest) {
      const { hasErrors, newErrors } = validateData({
        legalRepreData: props.legalRepreData,
        countries: props.countries,
      });

      const { noErrors, addressData: validatedAddressData } = validateNoErrors(addressObj);

      if (!hasErrors && noErrors) {
        props.setLegalRepreData(props.legalRepreData);
      }

      setErrors(newErrors);
      setAddressObj(validatedAddressData);
      props.onValidateLegalRepreData(!hasErrors && noErrors);
    }
  }, [props.validate.validateRequest]);

  const handleOnChangeData = (value: string, field: string) => {
    const newData = { ...props.legalRepreData, [field]: value };
    props.setLegalRepreData(newData);
    setErrors((prevErrors: any) => ({ ...prevErrors, [field]: "" }));
  };

  const handleOnChangeAddress = (addressObj: AddressDataDto) => {
    setAddressObj(addressObj);
    const newAddressData = getValidFormattedData(addressObj, false);
    const newData = { ...props.legalRepreData, address: newAddressData };
    props.setLegalRepreData(newData);
  };

  const getFilteredCountries = (): { [key: string]: string }[] => {
    const countries = props.countries as unknown as { [key: string]: string }[];
    if (!nationalityLabel || nationalityLabel.length < 2) return countries;

    return filterCountries(nationalityLabel, props.countries);
  };

  const getInitialNationalityValue = () => {
    const countries = props.countries;
    const nationality = countries.find((item) => item.value === props.legalRepreData.nationality);
    return nationality && nationality.label;
  };

  return (
    <RepreDataContainer>
      <div className="personal-data-wrapper">
        <div className="personal-data-fisica">
          <div className="data-title" id="data-personal">
            <h5>REPRESENTANTE LEGAL</h5>
          </div>
          <div className="personal-data-row">
            <div className="personal-data-item col-50">
              <div className="inputBox">
                <InputBox
                  placeholder="Nombre"
                  errorCode={errors.name}
                  value={props.legalRepreData.name}
                  onChange={(value: string) => handleOnChangeData(value.toUpperCase(), "name")}
                  type="letters"
                  required
                  notScroll
                />
              </div>
            </div>

            <div className="personal-data-item col-50">
              <div className="inputBox">
                <InputBox
                  placeholder="Apellido 1"
                  errorCode={errors.lastname1}
                  value={props.legalRepreData.lastname1}
                  onChange={(value: string) => handleOnChangeData(value.toUpperCase(), "lastname1")}
                  type="letters"
                  required
                  notScroll
                />
              </div>
            </div>
          </div>
          <div className="personal-data-row">
            <div className="personal-data-item col-50">
              <div className="inputBox">
                <InputBox
                  placeholder="Apellido 2"
                  errorCode={errors.lastname2}
                  value={props.legalRepreData.lastname2 || ""}
                  onChange={(value: string) => handleOnChangeData(value.toUpperCase(), "lastname2")}
                  type="letters"
                  required={props.legalRepreData.nationality === "ESPA"}
                  notScroll
                />
              </div>
            </div>
            <div className="personal-data-item col-50">
              <div className="inputBox">
                <CalendarBox
                  initialValue={props.legalRepreData.birthDate}
                  placeholder="Fecha de nacimiento"
                  forbidFutureDates
                  minAge={18}
                  maxDate={moment().toISOString()}
                  required={true}
                  errorCode={errors.birthDate}
                  onChange={(value: string) => handleOnChangeData(value.toUpperCase(), "birthDate")}
                  health
                  notScroll
                />
              </div>
            </div>
          </div>

          <div className="personal-data-row">
            <div className="personal-data-item col-50">
              <div className="selectBox">
                <SelectBox
                  optionsText={props.relationshipList}
                  optionKey="value"
                  optionValue="label"
                  defaultValue={props.legalRepreData.relationship}
                  placeholder="Relación con el menor"
                  errorCode={errors.relationship}
                  propValue={props.legalRepreData.relationship}
                  onChange={(value: string) =>
                    handleOnChangeData(value.toUpperCase(), "relationship")
                  }
                  required
                  isMediator
                />
                <label>Relación con el menor</label>
              </div>
            </div>
            <div className="personal-data-item col-50">
              <div className="inputBox">
                <SearchBox
                  onChange={(ev: any) => setNationalityLabel(ev.target.value.toUpperCase())}
                  labelText=""
                  optionsText={getFilteredCountries()}
                  optionKey={"value"}
                  optionValue={"label"}
                  initialValue={getInitialNationalityValue()}
                  required={true}
                  errorCode={errors.nationality}
                  onSelect={(item: any) => handleOnChangeData(item.value, "nationality")}
                  notScroll
                />
                <label>Nacionalidad</label>
              </div>
            </div>
          </div>

          <div className="personal-data-container">
            <div className="personal-data-row">
              <div className="personal-data-item col-50">
                <div className="selectBox">
                  <SelectBox
                    placeholder="Documento de identidad"
                    required
                    errorCode={errors.docType}
                    optionsText={getDocumentTypes(props.legalRepreData.nationality, true, true)}
                    optionKey="value"
                    optionValue="label"
                    initialValue={props.legalRepreData.docType}
                    defaultValue={props.legalRepreData.docType}
                    onChange={(value: string) => handleOnChangeData(value.toUpperCase(), "docType")}
                    isMediator
                  />
                  <label>Documento de identidad</label>
                </div>
              </div>
              <div className="personal-data-item col-50">
                <div className="inputBox">
                  <InputBox
                    placeholder="Nº de documento"
                    required
                    errorCode={errors.docNumber}
                    maxLength={DOC_NUMBER_MAX_LENGTH}
                    value={props.legalRepreData.docNumber}
                    onChange={(value: string) =>
                      handleOnChangeData(value.toUpperCase(), "docNumber")
                    }
                    type={(props.legalRepreData.docType as DocType) || "NIF"}
                    notScroll
                  />
                </div>
              </div>

              {props.legalRepreData.docType === "PAS" && (
                <div className="personal-data-item col-50">
                  <div className="inputBox">
                    <CalendarBox
                      initialValue={props.legalRepreData.passportDate || ""}
                      placeholder="Fecha de caducidad del pasaporte"
                      minDate={moment().toISOString()}
                      required={true}
                      errorCode={errors.passportDate}
                      onChange={(value: string) => handleOnChangeData(value, "passportDate")}
                      health={true}
                      notScroll
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <RepreAddress
        validate={{
          validateRequest: props.validate.validateRequest,
          isValidated: false,
        }}
        physicalPerson={true}
        onValidated={() => {}}
        setLoading={() => {}}
        onAnyChange={props.onAnyChange}
        onChangeAddress={handleOnChangeAddress}
        legalRepreAddress={props.legalRepreData.address}
        addressObj={addressObj}
      />

      <div className="content-wrapper bottomMargin20">
        <div className="personal-data-wrapper" id="data-contact">
          <div className="data-title">
            <h5>DATOS CONTACTO REPRESENTANTE LEGAL</h5>
          </div>
          <div className="colSin-100">
            <div className="colSin-50 rightPadding20 topMargin20 bottomMargin20">
              <div className="inputBox">
                <PhoneBox
                  errorCode={errors.mobilePhone}
                  onChange={(value: string) =>
                    handleOnChangeData(value.toUpperCase(), "mobilePhone")
                  }
                  initialValue={props.legalRepreData.mobilePhone}
                  labelText
                  required
                  notScroll
                />
                <label>teléfono móvil</label>
              </div>
            </div>
            <div className="colSin-50 rightPadding20 topMargin20 bottomMargin20">
              <div className="inputBox">
                <InputBox
                  errorCode={errors.landlinePhone}
                  onChange={(value: string) =>
                    handleOnChangeData(value.toUpperCase(), "landlinePhone")
                  }
                  placeholder="TELÉFONO FIJO"
                  type="number"
                  value={props.legalRepreData.landlinePhone || ""}
                  maxLength={12}
                  notScroll
                />
              </div>
            </div>

            <div className="colSin-100 rightPadding20 bottomMargin20">
              <div className="inputBox">
                <InputBox
                  errorCode={errors.email}
                  onChange={(value: string) => handleOnChangeData(value, "email")}
                  onBlur={(value: string) => handleOnChangeData(value.toUpperCase(), "email")}
                  placeholder="CORREO ELECTRÓNICO"
                  type="email"
                  value={props.legalRepreData.email}
                  required
                  notScroll
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </RepreDataContainer>
  );
};

export default withGeneral(LegalRepreData);
